import { FC } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Svg, Typography, useThemeValue } from '@altermaind/web/ui';
import { BlockingDialog } from '@/_shared/components/BlockingDialog';
import { convertColorToRgba } from '@/_shared/helpers/convertColorToRgba';
import { openTabForAsyncRedirect } from '@/_shared/helpers/openTabForAsyncRedirect';
import { useDialog } from '@/_shared/hooks/useDialog';
import { getBillingLinkCall } from '@/billing/state/api/getBillingLinkCall';
import type { DialogContactsPayload } from '@/core/components/DialogContacts/types';
import { getUserAuthorizeCall } from '@/core/state/api/getUserAuthorizeCall';
import { UserAuthErrorType } from '@/swagger/constants/slash-enums';

export const DialogPaymentFailed: FC = () => {
  const t = useTranslations('billing.dialogPaymentFailed');
  const { blockingErrorType } = getUserAuthorizeCall.hook();
  const { execute: executeGetBillingLink } = getBillingLinkCall.hook();
  const errorBg = useThemeValue('colors', 'background/error');

  const { openDialog: openContacts } = useDialog<DialogContactsPayload>('contacts');

  const isOpen = blockingErrorType === UserAuthErrorType.SUBSCRIPTION_PAYMENT_FAILED;

  const handleUpdatePaymentMethod = () => {
    const { redirect, close } = openTabForAsyncRedirect();

    executeGetBillingLink({
      successSaga: ({ response }) => {
        if (response?.link) {
          redirect(response.link);
        } else {
          close();
        }
      },
      errorSaga: () => {
        close();
      },
    });
  };

  return (
    <BlockingDialog name='paymentFailed' open={isOpen} dialogProps={{ maxWidth: '900px' }}>
      <Box
        display='flex'
        mb='m'
        flexDirection='column'
        alignItems='center'
        alignSelf='center'
        bg={errorBg ? (convertColorToRgba(errorBg, 0.1) as never) : 'background/error'}
        p='xs'
        borderRadius='100%'
      >
        <Svg name='warning' size='xxl' stroke='error' />
      </Box>
      <Typography variant='h3' mt='none' textAlign='center' mb='s' color='primary/text'>
        {t('title')}
      </Typography>
      <Typography variant='p2/light' textAlign='center' mb='l' color='grey/light'>
        {t('description')}
      </Typography>
      <Box display='flex' flexDirection='column' alignItems='center'>
        <Button variant='primary' onClick={handleUpdatePaymentMethod}>
          {t('updatePaymentMethod')}
        </Button>
        <Button variant='link/primary' mt='s' onClick={() => openContacts({})}>
          {t('contactUs')}
        </Button>
      </Box>
    </BlockingDialog>
  );
};
